import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { getWorkspace, setWorkspace as setActiveWorkspace } from "../lib/workspaceStore";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const ROLE_COLORS = {
  owner: "bg-amber-100 text-amber-800",
  admin: "bg-blue-100 text-blue-800",
  client: "bg-gray-100 text-gray-800",
};

export default function MembersPage() {
  const [workspace, setWorkspace] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("client");
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    loadWorkspaces();
  }, []);

  useEffect(() => {
    if (workspace) {
      loadMembers(workspace.id);
    }
  }, [workspace]);

  async function loadWorkspaces() {
    setLoading(true);
    try {
      const res = await api.get("/api/workspaces");
      setWorkspaces(res.data.items || []);

      const stored = getWorkspace();
      const wsList = res.data.items || [];
      if (stored && wsList.find((w) => w.id === stored.id)) {
        setWorkspace(stored);
      } else if (wsList.length > 0) {
        setWorkspace(wsList[0]);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load workspaces.");
    } finally {
      setLoading(false);
    }
  }

  async function loadMembers(wid) {
    setErr(null);
    try {
      const res = await api.get(`/api/workspaces/${wid}/members`);
      setMembers(res.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load members.");
    }
  }

  async function invite() {
    if (!workspace || !email.trim()) return;
    setErr(null);
    setSuccess(null);
    setInviting(true);

    try {
      await api.post(`/api/workspaces/${workspace.id}/members`, {
        email: email.trim().toLowerCase(),
        role,
      });
      setEmail("");
      setSuccess(`Successfully invited ${email} as ${role}.`);
      await loadMembers(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to invite member.");
    } finally {
      setInviting(false);
    }
  }

  async function updateRole(userId, newRole) {
    if (!workspace) return;
    setErr(null);
    setSuccess(null);

    try {
      await api.patch(`/api/workspaces/${workspace.id}/members/${userId}`, {
        role: newRole,
      });
      setSuccess("Role updated successfully.");
      await loadMembers(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to update role.");
    }
  }

  async function removeMember(userId, memberEmail) {
    if (!workspace) return;
    if (!window.confirm(`Remove ${memberEmail || userId} from workspace?`)) return;

    setErr(null);
    setSuccess(null);

    try {
      await api.delete(`/api/workspaces/${workspace.id}/members/${userId}`);
      setSuccess("Member removed successfully.");
      await loadMembers(workspace.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to remove member.");
    }
  }

  if (loading) {
    return (
      <div className="space-y-4" data-testid="members-page">
        <div className="text-2xl font-semibold">Team Members</div>
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Loading...
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="space-y-4" data-testid="members-page">
        <div className="text-2xl font-semibold">Team Members</div>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="py-4">
            <div className="text-sm text-muted-foreground">No workspace selected.</div>
            <Link to="/app/workspaces">
              <Button className="mt-3">Go to Workspaces</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-2xl font-semibold">Team Members</div>
          <div className="text-sm text-muted-foreground">
            Manage who has access to this workspace
          </div>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={workspace.id}
            onChange={(e) => {
              const ws = workspaces.find((x) => x.id === e.target.value);
              if (ws) setActiveWorkspace(ws);
            }}
            className="rounded-md border px-2 py-1 text-sm"
            data-testid="workspace-select"
          >
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Alerts */}
      {err && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-700">{err}</CardContent>
        </Card>
      )}

      {success && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-3 text-sm text-green-700">{success}</CardContent>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Invite Member Card */}
        <Card>
          <CardHeader>
            <CardTitle>Invite Member</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email address</label>
              <Input
                type="email"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <select
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="invite-role-select"
              >
                <option value="client">Client - View outputs only</option>
                <option value="admin">Admin - Create and run packs</option>
              </select>
            </div>

            <Button
              onClick={invite}
              disabled={inviting || !email.trim()}
              className="w-full"
              data-testid="invite-btn"
            >
              {inviting ? "Inviting..." : "Send Invite"}
            </Button>

            <div className="text-xs text-muted-foreground">
              Note: The user must have an existing account to be invited.
            </div>
          </CardContent>
        </Card>

        {/* Current Members Card */}
        <Card>
          <CardHeader>
            <CardTitle>Current Members ({members.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {members.length === 0 ? (
              <div className="text-sm text-muted-foreground">No members found.</div>
            ) : (
              <div className="space-y-2">
                {members.map((m) => (
                  <div
                    key={m.user_id}
                    className="flex items-center justify-between rounded-lg border p-3"
                    data-testid={`member-${m.user_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-sm font-medium">
                        {(m.email || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        <div className="font-medium">{m.email || m.user_id}</div>
                        <Badge className={ROLE_COLORS[m.role] || "bg-gray-100"}>
                          {m.role}
                        </Badge>
                      </div>
                    </div>

                    {m.role !== "owner" && (
                      <div className="flex gap-2">
                        <select
                          value={m.role}
                          onChange={(e) => updateRole(m.user_id, e.target.value)}
                          className="rounded border px-2 py-1 text-xs"
                          data-testid={`role-select-${m.user_id}`}
                        >
                          <option value="client">Client</option>
                          <option value="admin">Admin</option>
                        </select>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeMember(m.user_id, m.email)}
                          data-testid={`remove-btn-${m.user_id}`}
                        >
                          Remove
                        </Button>
                      </div>
                    )}

                    {m.role === "owner" && (
                      <div className="text-xs text-muted-foreground">Owner</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Role Info */}
      <Card className="bg-muted/30">
        <CardContent className="py-4">
          <div className="text-sm font-medium mb-2">Role Permissions</div>
          <div className="grid gap-2 text-sm text-muted-foreground md:grid-cols-3">
            <div>
              <span className="font-medium text-amber-700">Owner:</span> Full access, manage members
            </div>
            <div>
              <span className="font-medium text-blue-700">Admin:</span> Create/edit/run packs
            </div>
            <div>
              <span className="font-medium text-gray-700">Client:</span> View outputs only
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
